import { useQuery } from "@tanstack/react-query";
import { Activity, Cloud, Server } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { getHealth, getMinewESLStatus } from "@/api/auth";
import { getTagsOverview } from "@/api/stores";
import { Card } from "@/components/ui/Card";
import { useAuth } from "@/hooks/useAuth";

function formatTokenAge(seconds: number | null): string {
  if (seconds === null) return "—";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <span
      className={ok ? "text-emerald-500" : "text-destructive"}
      aria-hidden="true"
    >
      ●
    </span>
  );
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const { data: health, isLoading } = useQuery({
    queryKey: ["health"],
    queryFn: getHealth,
    refetchInterval: 10_000,
  });

  const { data: minewesl, isLoading: minewESLLoading } = useQuery({
    queryKey: ["minewesl-status"],
    queryFn: getMinewESLStatus,
    refetchInterval: 30_000,
  });

  const { data: tagsOverview, isLoading: tagsLoading } = useQuery({
    queryKey: ["tags-overview"],
    queryFn: () => getTagsOverview(true),
    refetchInterval: 60_000,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t("dashboard.title")}</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {t("dashboard.welcome", { name: user?.full_name ?? user?.email ?? "" })}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card
          title={
            <span className="flex items-center gap-1.5">
              <Server size={13} className="text-muted-foreground" />
              Backend status
            </span>
          }
        >
          {isLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : health ? (
            <div className="space-y-1.5">
              <p className="text-2xl font-bold">
                <StatusDot ok={health.status === "ok"} />{" "}
                {t(`dashboard.health.${health.status === "ok" ? "ok" : "degraded"}`)}
              </p>
              <p className="font-mono text-xs text-muted-foreground">
                db: {health.database} · v{health.version}
              </p>
            </div>
          ) : (
            <p className="text-sm text-destructive">{t("dashboard.health.error")}</p>
          )}
        </Card>

        <Card
          title={
            <span className="flex items-center gap-1.5">
              <Activity size={13} className="text-muted-foreground" />
              {t("dashboard.tags.title")}
            </span>
          }
        >
          {tagsLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : tagsOverview ? (
            <div className="space-y-1.5">
              <p className="text-2xl font-bold tabular-nums">{tagsOverview.total_tags}</p>
              <p className="text-xs text-muted-foreground">
                {t("dashboard.tags.stores", { count: tagsOverview.stores.length })}
                {tagsOverview.total_warnings > 0 && (
                  <>
                    {" · "}
                    <span className="text-amber-600">
                      {t("dashboard.tags.warnings", { count: tagsOverview.total_warnings })}
                    </span>
                  </>
                )}
              </p>
              {tagsOverview.stores.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                  {tagsOverview.stores.slice(0, 3).map((s) => (
                    <li key={s.store_id} className="flex justify-between">
                      <Link
                        to={`/stores/${s.store_id}`}
                        className="truncate hover:text-foreground"
                      >
                        {s.store_name}
                      </Link>
                      <span className="tabular-nums">{s.tag_count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <p className="text-sm text-destructive">{t("common.error")}</p>
          )}
        </Card>

        <Card
          title={
            <span className="flex items-center gap-1.5">
              <Cloud size={13} className="text-muted-foreground" />
              MinewESL connection
            </span>
          }
        >
          {minewESLLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : minewesl ? (
            <div className="space-y-1.5">
              <p className="text-2xl font-bold">
                <StatusDot ok={minewesl.connected} />{" "}
                {minewesl.connected
                  ? t("dashboard.minewesl.connected")
                  : t("dashboard.minewesl.disconnected")}
              </p>
              <p className="font-mono text-xs text-muted-foreground">
                {minewesl.username} ·{" "}
                {t("dashboard.minewesl.fields", { count: minewesl.fields_count ?? 0 })} ·{" "}
                {t("dashboard.minewesl.token_age", {
                  age: formatTokenAge(minewesl.token_age_seconds),
                })}
              </p>
              <p className="font-mono text-xs text-muted-foreground">
                {t("dashboard.minewesl.request_rate", {
                  rate: minewesl.request_rate_per_min,
                })}
              </p>
              {minewesl.last_error && (
                <p
                  className="break-all text-xs text-destructive"
                  title={minewesl.last_error}
                >
                  {minewesl.last_error.slice(0, 80)}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-destructive">{t("common.error")}</p>
          )}
        </Card>
      </div>
    </div>
  );
}
